import { Sender } from 'modules/chatbot/enums/Sender';

// Base interfaces
export interface BaseEntity {
  id: string;
  createdAt: number;
  updatedAt: number;
  metadata?: Record<string, any>;
}

// Message related types
export enum MessageStatus {
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  STREAMING = 'STREAMING',
  COMPLETED = 'COMPLETED',
}

export interface MessageMetadata {
  retryCount?: number;
  errorCode?: string;
  errorMessage?: string;
  responseTime?: number;
  conversationContext?: string;
  userAgent?: string;
  sessionId?: string;
  eventType?: EventType;
  steps?: string[];
}

export interface ChatMessage extends BaseEntity {
  sender: Sender;
  content: string;
  reference?: string;
  timestamp: number;
  status: MessageStatus;
  retryCount?: number;
  metadata?: MessageMetadata;
  sources?: any[]; // Sources that come with the complete response
}

export interface ChunkAnimationType {
  isTyping: boolean;
  messageId: string | null;
  chunkCount?: number | null;
}

// UI State types
export interface ChatUIState {
  isVisible: boolean;
  isPending: boolean;
  animateTyping: ChunkAnimationType;
  isConnected: boolean;
  error: ChatError | null;
  retryAttempts: number;
  lastActivity: number;
  inputValue: string;
  isMinimized: boolean;
  isFullScreen: boolean;
  showNewChatOverlay: boolean;
  showPreparingOverlay: boolean;
  initializationStatus: 'idle' | 'loading' | 'success' | 'error';
  initializationError: string | null;
}

// Error handling types
export interface ChatError {
  code: ErrorCode;
  message: string;
  timestamp: number;
  context?: Record<string, any>;
  userMessage?: string; // User-friendly message to display
  technicalDetails?: string; // Technical details for debugging
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

export type ErrorCode =
  | 'NETWORK_ERROR'
  | 'TIMEOUT_ERROR'
  | 'VALIDATION_ERROR'
  | 'SERVER_ERROR'
  | 'RATE_LIMIT_ERROR'
  | 'AUTHENTICATION_ERROR'
  | 'UNKNOWN_ERROR'
  | 'SSE_CONNECTION_ERROR'
  | 'SSE_TIMEOUT_ERROR'
  | 'SSE_MALFORMED_MESSAGE'
  | 'SSE_STREAM_INTERRUPTED'
  | 'SSE_INCOMPLETE_RESPONSE'
  | 'CONNECTION_LOST'
  | 'RETRY_EXHAUSTED'
  | 'CHUNK_DECODING_ERROR';

export enum EventType {
  STATUS = 'status',
  CHUNK = 'chunk',
  SOURCES = 'sources',
  COMPLETE = 'complete',
  ERROR = 'error',
  RESPONSE = 'response',
  ACTION = 'action',
}

export enum ActionType {
  PLAN_RECOMMENDER = 'plan_recommender',
}

export interface SSEMessage {
  type: EventType;
  data: any;
  timestamp: number;
  id?: string;
}

export interface SSEConfig {
  url: string;
  reconnectDelay: number;
  maxReconnectAttempts: number;
  chunkTimeout?: number; // Timeout between SSE chunks
  responseTimeout?: number; // Overall response timeout
  heartbeatInterval?: number; // Heartbeat to detect connection issues
}

export interface SSEEventHandlers {
  onMessage?: (message: SSEMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: ChatError) => void;
  onReconnect?: (attempt: number) => void;
  onStateChange?: (state: string) => void;
  onComplete?: () => void;
}

// Component prop types
export interface ChatContainerProps {
  visible: boolean;
  onVisibilityChange: (visible: boolean) => void;
  className?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

// Hook types
export interface UseChatReturn {
  messages: ChatMessage[];
  sendMessage: (content: string) => Promise<void>;
  clearChat: () => void;
  isLoading: boolean;
  error: ChatError | null;
  clearError: () => void;
  uiState: ChatUIState;
}

export interface UseChatOptions {
  conversationId?: string;
  autoConnect?: boolean;
  enableRetry?: boolean;
  maxRetries?: number;
  metadata?: Record<string, any>;
}

// Validation types
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}
